import React from 'react';
import Navigation from './Navigation';
import Footer from './Footer';
import CTA from './CTA';
import '../LandingPage.css';
import './HomePageV2.css';

function HomePageV2() {
  // Top Value - What Enterprises Care About Most
  const topValueFeatures = [
    {
      title: '100% Deterministic Results on Demand',
      description: 'Control your LLM and decision logic with absolute precision—no black box, no unpredictability.',
      icon: (
        <svg width="48" height="48" viewBox="0 0 32 32" fill="none">
          <rect x="4" y="4" width="24" height="24" rx="4" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M12 16 L16 20 L24 12" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      title: 'Absolute Explainability & Full Auditability',
      description: 'Every decision, every node, every connection—fully visible, traceable, and transparent.',
      icon: (
        <svg width="48" height="48" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M16 8 L16 16 L20 20" stroke="currentColor" strokeWidth="2"/>
          <circle cx="16" cy="24" r="1.5" fill="currentColor"/>
        </svg>
      )
    },
    {
      title: 'End-to-End Process Automation With Human-Like Reasoning',
      description: 'Encode expert logic, rules, context, and machine learning into a single intelligent graph.',
      icon: (
        <svg width="48" height="48" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="8" r="4" fill="currentColor"/>
          <circle cx="8" cy="20" r="4" fill="currentColor"/>
          <circle cx="24" cy="20" r="4" fill="currentColor"/>
          <line x1="16" y1="12" x2="10" y2="18" stroke="currentColor" strokeWidth="2"/>
          <line x1="16" y1="12" x2="22" y2="18" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      title: 'Massive Savings & Unprecedented Efficiency',
      description: 'Eliminate re-training loops, reduce compute cost, accelerate workflows, and scale enterprise operations.',
      icon: (
        <svg width="48" height="48" viewBox="0 0 32 32" fill="none">
          <path d="M4 20 L12 12 L20 16 L28 8" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="12" r="3" fill="currentColor"/>
          <circle cx="20" cy="16" r="3" fill="currentColor"/>
        </svg>
      )
    }
  ];

  // Breakthrough Innovation - Why It Beats Today's AI Stack
  const breakthroughFeatures = [
    {
      title: 'One Graph. Ten Functions. Infinite Intelligence.',
      description: 'Knowledge, rules, processes, ML models, context, workflows—all unified in a single adaptive graph.',
      whyItMatters: 'Traditional AI requires separate systems for each function. CerebralMachines unifies everything.',
      icon: (
        <svg width="64" height="64" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="16" cy="16" r="6" fill="currentColor"/>
          <circle cx="8" cy="8" r="2" fill="currentColor"/>
          <circle cx="24" cy="8" r="2" fill="currentColor"/>
          <circle cx="8" cy="24" r="2" fill="currentColor"/>
          <circle cx="24" cy="24" r="2" fill="currentColor"/>
          <line x1="12" y1="12" x2="8" y2="8" stroke="currentColor" strokeWidth="1"/>
          <line x1="20" y1="12" x2="24" y2="8" stroke="currentColor" strokeWidth="1"/>
          <line x1="12" y1="20" x2="8" y2="24" stroke="currentColor" strokeWidth="1"/>
          <line x1="20" y1="20" x2="24" y2="24" stroke="currentColor" strokeWidth="1"/>
        </svg>
      )
    },
    {
      title: 'Beyond RAG: Human-Brain-Like Knowledge Representation',
      description: 'Context-rich, cross-domain reasoning that surpasses vector search and prompt chaining.',
      whyItMatters: 'Move beyond simple retrieval to true understanding with interconnected knowledge.',
      icon: (
        <svg width="64" height="64" viewBox="0 0 32 32" fill="none">
          <path d="M8 16 L16 8 L24 16 L16 24 Z" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="16" cy="16" r="4" fill="currentColor"/>
          <path d="M4 16 L8 16" stroke="currentColor" strokeWidth="2"/>
          <path d="M24 16 L28 16" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      title: 'Advanced Rule-Based Ontology',
      description: 'Expert logic expressed as formal, reusable, machine-interpretable rule structures.',
      whyItMatters: 'Capture domain expertise in a form that machines can execute and humans can verify.',
      icon: (
        <svg width="64" height="64" viewBox="0 0 32 32" fill="none">
          <rect x="4" y="6" width="24" height="4" rx="2" fill="currentColor"/>
          <rect x="4" y="14" width="24" height="4" rx="2" fill="currentColor"/>
          <rect x="4" y="22" width="24" height="4" rx="2" fill="currentColor"/>
        </svg>
      )
    },
    {
      title: 'Deterministic Process Representation',
      description: 'Codify business processes with exact, repeatable execution—no drift, no ambiguity.',
      whyItMatters: 'Every process runs identically every time, ensuring consistency and compliance.',
      icon: (
        <svg width="64" height="64" viewBox="0 0 32 32" fill="none">
          <circle cx="6" cy="16" r="4" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="16" cy="16" r="4" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="26" cy="16" r="4" stroke="currentColor" strokeWidth="2" fill="none"/>
          <line x1="10" y1="16" x2="12" y2="16" stroke="currentColor" strokeWidth="2"/>
          <line x1="20" y1="16" x2="22" y2="16" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      title: 'Live ML Model Adaptation',
      description: 'Update model behavior instantly—no retrain, no redeploy, no downtime.',
      whyItMatters: 'Adapt to changing conditions in real-time without interrupting operations.',
      icon: (
        <svg width="64" height="64" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="12" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M16 4 L16 16 L22 22" stroke="currentColor" strokeWidth="2"/>
          <circle cx="16" cy="16" r="2" fill="currentColor"/>
        </svg>
      )
    }
  ];

  // Deep Technical Differentiators
  const technicalFeatures = [
    {
      title: 'Machine Learning + Rules + Processes + Data: All in One Graph',
      description: 'The only system that fuses symbolic reasoning, neural models, domain rules, and process automation seamlessly.',
      details: [
        'Symbolic reasoning for deterministic logic',
        'Cognitive models for pattern recognition',
        'Domain rules for business constraints',
        'Process automation for workflows'
      ],
      icon: (
        <svg width="56" height="56" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="8" r="3" fill="currentColor"/>
          <circle cx="8" cy="16" r="3" fill="currentColor"/>
          <circle cx="24" cy="16" r="3" fill="currentColor"/>
          <circle cx="16" cy="24" r="3" fill="currentColor"/>
          <line x1="16" y1="11" x2="10" y2="14" stroke="currentColor" strokeWidth="2"/>
          <line x1="16" y1="11" x2="22" y2="14" stroke="currentColor" strokeWidth="2"/>
          <line x1="10" y1="18" x2="14" y2="21" stroke="currentColor" strokeWidth="2"/>
          <line x1="22" y1="18" x2="18" y2="21" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      title: 'Massive Cross-Domain Knowledge Integration',
      description: 'Unify policies, regulations, procedures, scientific knowledge, and system data into one living intelligence layer.',
      details: [
        'Policies and regulations as graph nodes',
        'Procedural knowledge encoded explicitly',
        'Scientific knowledge linked to applications',
        'Real-time system data integrated seamlessly'
      ],
      icon: (
        <svg width="56" height="56" viewBox="0 0 32 32" fill="none">
          <rect x="4" y="4" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
          <rect x="18" y="4" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
          <rect x="4" y="18" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
          <rect x="18" y="18" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
          <line x1="14" y1="9" x2="18" y2="9" stroke="currentColor" strokeWidth="2"/>
          <line x1="9" y1="14" x2="9" y2="18" stroke="currentColor" strokeWidth="2"/>
          <line x1="14" y1="23" x2="18" y2="23" stroke="currentColor" strokeWidth="2"/>
          <line x1="23" y1="14" x2="23" y2="18" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      title: 'Ultra-Low Energy Consumption',
      description: 'Event-driven architectures drastically reduce compute usage and cost.',
      details: [
        'Event-driven computation reduces waste',
        'Only active nodes consume energy',
        'Efficient graph traversal algorithms',
        'Up to 90% reduction vs traditional ML'
      ],
      icon: (
        <svg width="56" height="56" viewBox="0 0 32 32" fill="none">
          <path d="M16 4 L12 12 L20 12 Z" fill="currentColor"/>
          <rect x="14" y="12" width="4" height="16" fill="currentColor"/>
          <circle cx="16" cy="30" r="1.5" fill="currentColor"/>
        </svg>
      )
    },
    {
      title: 'Rich, Multi-Layered Context Formation',
      description: 'A holistic understanding of every scenario—far deeper than embeddings or prompts can offer.',
      details: [
        'Multiple context layers simultaneously',
        'Historical, current, and predictive context',
        'Cross-domain relationships considered',
        'Dynamic context updates in real-time'
      ],
      icon: (
        <svg width="56" height="56" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="16" cy="16" r="6" stroke="currentColor" strokeWidth="2" fill="none"/>
          <circle cx="16" cy="16" r="2" fill="currentColor"/>
        </svg>
      )
    },
    {
      title: 'Live Integration With Real-Time System Data',
      description: 'Static knowledge meets live events for continuous intelligence across enterprise systems.',
      details: [
        'Stream processing for real-time events',
        'Graph updates propagate instantly',
        'Event-driven workflow triggers',
        'Continuous learning from live data'
      ],
      icon: (
        <svg width="56" height="56" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="8" stroke="currentColor" strokeWidth="2" fill="none"/>
          <path d="M16 8 L16 16 L20 20" stroke="currentColor" strokeWidth="2"/>
          <circle cx="16" cy="28" r="2" fill="currentColor"/>
        </svg>
      )
    }
  ];

  return (
    <div className="landing-page homepage-v2">
      <Navigation />
      
      {/* Enhanced Hero Section */}
      <section className="hero hero-v2">
        <div className="hero-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="grid-pattern"></div>
        </div>

        <div className="hero-container hero-container-v2">
          <div className="hero-content hero-content-v2">
            <h1 className="hero-title hero-title-v2">
              Cerebral Graphs <br></br>
              <span className="gradient-text">A breakthrough innovation for enterprises AI</span>
            </h1>
            
            <p className="hero-subtitle hero-subtitle-v2">
              The First AI That Thinks in Graphs, Reasons Like Experts, and Adapts Like the Brain
            </p>

            <div className="hero-actions">
              <a href="/signup">
                <button className="btn-primary btn-large">
                  Get a Demo
                </button>
              </a>
              <a href="#top-value">
                <button className="btn-outline btn-large">
                  Explore Features
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Top Value Section - Priority Layout (2x2 prominent grid) */}
      <section className="section-divider"></section>
      <section className="top-value-section" id="top-value">
        <div className="section-container">
          <div className="section-header section-header-v2">
            <h2 className="section-title section-title-v2">Tech to take stuck enterprise AI forward</h2>
            <p className="section-subtitle section-subtitle-v2">
              Enterprise-grade capabilities that deliver real business value and measurable ROI
            </p>
          </div>

          <div className="top-value-grid">
            {topValueFeatures.map((feature, index) => (
              <div key={index} className="top-value-card">
                <div className="top-value-icon">{feature.icon}</div>
                <h3 className="top-value-title">{feature.title}</h3>
                <p className="top-value-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Breakthrough Innovation Section - Alternating layout */}
      <section className="section-divider"></section>
      <section className="breakthrough-section" id="breakthrough">
        <div className="section-container">
          <div className="section-header section-header-v2">
            <h2 className="section-title section-title-v2">Breakthrough Innovation — The missing tech in AI stack is here</h2>
            <p className="section-subtitle section-subtitle-v2">
              Revolutionary capabilities that go beyond traditional AI and machine learning approaches
            </p>
          </div>

          <div className="breakthrough-list">
            {breakthroughFeatures.map((feature, index) => (
              <div key={index} className={`breakthrough-item ${index % 2 === 0 ? 'breakthrough-item-left' : 'breakthrough-item-right'}`}>
                <div className="breakthrough-icon-wrapper">
                  <div className="breakthrough-icon">{feature.icon}</div>
                </div>
                <div className="breakthrough-content">
                  <h3 className="breakthrough-title">{feature.title}</h3>
                  <p className="breakthrough-description">{feature.description}</p>
                  <div className="breakthrough-why">
                    <strong>Why it matters:</strong> {feature.whyItMatters}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Technical Differentiators - Detailed layout */}
      <section className="section-divider"></section>
      <section className="technical-section" id="technical">
        <div className="section-container">
          <div className="section-header section-header-v2">
            <h2 className="section-title section-title-v2">Deep Technical Differentiators</h2>
            <p className="section-subtitle section-subtitle-v2">
              Advanced technical capabilities that set CerebralMachines apart from any other AI platform
            </p>
          </div>

          <div className="technical-grid">
            {technicalFeatures.map((feature, index) => (
              <div key={index} className="technical-card">
                <div className="technical-icon-wrapper">
                  <div className="technical-icon">{feature.icon}</div>
                </div>
                <h3 className="technical-title">{feature.title}</h3>
                <p className="technical-description">{feature.description}</p>
                <ul className="technical-details">
                  {feature.details.map((detail, idx) => (
                    <li key={idx} className="technical-detail-item">{detail}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
}

export default HomePageV2;